/**
 * LoreModal component.
 * Parchment-styled modal for viewing and editing lore entries attached to sub-hexes.
 * Supports view mode (read-only) and edit mode (for hex owners).
 */

import { useState, useEffect } from 'react';
import { Editor } from '@/components/Editor/Editor';
import { EditorRenderer } from '@/components/Editor/EditorRenderer';
import type { SubHexLoreEntry } from './types';

interface LoreModalProps {
    /** Whether the modal is visible */
    isOpen: boolean;
    /** Callback to close the modal */
    onClose: () => void;
    /** Whether the user can edit (is hex owner) */
    canEdit: boolean;
    /** Current lore entry if it exists */
    loreEntry: SubHexLoreEntry | null;
    /** Full lore content (fetched separately) */
    loreContent: string | null;
    /** Whether content is loading */
    isLoading: boolean;
    /** Coordinates being viewed/edited */
    coordinates: { layerId: number; q: number; r: number };
    /** Callback when lore is saved */
    onSave: (title: string, content: string) => Promise<void>;
    /** Callback when lore is deleted */
    onDelete: () => Promise<void>;
}

/**
 * Decorative corner flourish SVG component for parchment borders.
 */
function CornerFlourish({ className = '' }: { className?: string }) {
    return (
        <svg
            className={`w-8 h-8 text-amber-800/30 ${className}`}
            viewBox="0 0 100 100"
            fill="currentColor"
        >
            <path d="M0,0 Q50,10 50,50 Q10,50 0,0 M10,0 Q40,20 40,40 Q20,40 10,0" />
        </svg>
    );
}

/**
 * Wax seal component for visual flair.
 */
function WaxSeal() {
    return (
        <div
            className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-600 to-amber-800"
            style={{
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3)',
            }}
        >
            <span className="font-serif text-amber-100/80 text-sm">L</span>
        </div>
    );
}

/**
 * Modal for viewing and editing lore entries.
 */
export function LoreModal({
    isOpen,
    onClose,
    canEdit,
    loreEntry,
    loreContent,
    isLoading,
    coordinates,
    onSave,
    onDelete,
}: LoreModalProps) {
    const [isEditMode, setIsEditMode] = useState(false);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Initialize form when lore entry changes
    useEffect(() => {
        if (loreEntry) {
            setTitle(loreEntry.title);
            setContent(loreContent || '');
        } else {
            setTitle('');
            setContent('');
            // If no lore exists and user can edit, start in edit mode
            if (canEdit) {
                setIsEditMode(true);
            }
        }
    }, [loreEntry, loreContent, canEdit]);

    // Reset edit mode when modal closes
    useEffect(() => {
        if (!isOpen) {
            setIsEditMode(false);
            setShowDeleteConfirm(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (!title.trim()) return;

        setIsSaving(true);
        try {
            await onSave(title.trim(), content);
            setIsEditMode(false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await onDelete();
            onClose();
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleCancel = () => {
        if (loreEntry) {
            // Reset to original values
            setTitle(loreEntry.title);
            setContent(loreContent || '');
            setIsEditMode(false);
        } else {
            // No lore exists, close modal
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            {/* Parchment modal container */}
            <div
                className="relative rounded-lg overflow-hidden w-full max-w-2xl max-h-[85vh] flex flex-col"
                style={{
                    background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5), inset 0 0 60px rgba(139,90,43,0.15)',
                }}
            >
                {/* Decorative corner flourishes */}
                <CornerFlourish className="absolute top-2 left-2" />
                <CornerFlourish className="absolute top-2 right-2 rotate-90" />
                <CornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                <CornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                {/* Aged paper edge effects */}
                <div
                    className="absolute top-0 left-0 right-0 h-2"
                    style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.15), transparent)' }}
                />
                <div
                    className="absolute bottom-0 left-0 right-0 h-2"
                    style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.15), transparent)' }}
                />

                {/* Content area */}
                <div className="relative p-6 flex flex-col flex-1 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <WaxSeal />
                            {isEditMode ? (
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter lore title..."
                                    className="text-xl font-serif text-amber-900 bg-white/50 border border-amber-400/50 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    autoFocus
                                />
                            ) : (
                                <h2 className="text-xl font-serif text-amber-900 tracking-wide">
                                    {loreEntry?.title || 'Untitled Lore'}
                                </h2>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="text-amber-800/60 hover:text-amber-900 transition-colors p-1"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Coordinate indicator */}
                    <p className="text-xs font-serif italic text-amber-700/60 mb-4">
                        Location: ({coordinates.q}, {coordinates.r})
                    </p>

                    {/* Decorative divider */}
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                        <span className="text-amber-800/40 text-sm">~</span>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                    </div>

                    {/* Main content area */}
                    <div className="flex-1 overflow-y-auto min-h-0">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-amber-800/60 font-serif italic">Loading lore...</div>
                            </div>
                        ) : isEditMode ? (
                            <div className="bg-white/50 rounded-lg overflow-hidden border border-amber-300/50">
                                <Editor
                                    value={content}
                                    onChange={(value) => setContent(value)}
                                    placeholder="Write your lore here... Tell the story of this place."
                                    height={300}
                                />
                            </div>
                        ) : loreEntry ? (
                            <div
                                className="prose prose-sm max-w-none"
                                style={{
                                    fontFamily: 'var(--font-handwriting)',
                                    fontSize: '1.1rem',
                                    lineHeight: '1.7',
                                }}
                            >
                                <EditorRenderer content={loreContent} theme="parchment" />
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-amber-800/60 font-serif italic">
                                    No lore has been written for this location yet.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Bottom flourish */}
                    <div className="flex items-center justify-center gap-2 mt-4 mb-2">
                        <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-800/30" />
                        <span className="text-amber-800/30 text-xs">~</span>
                        <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-800/30" />
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-amber-800/10">
                        <div>
                            {canEdit && loreEntry && !isEditMode && (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="px-3 py-1.5 text-red-700 hover:text-red-800 hover:bg-red-100/50 rounded transition-colors font-serif text-sm"
                                >
                                    Delete
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            {isEditMode ? (
                                <>
                                    <button
                                        onClick={handleCancel}
                                        className="px-4 py-2 bg-amber-200/50 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors font-serif border border-amber-400/30"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={!title.trim() || isSaving}
                                        className="px-5 py-2 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 disabled:from-amber-700 disabled:to-amber-800 disabled:opacity-60 rounded-lg font-serif font-medium transition-all shadow-md border border-amber-500/30 text-white"
                                    >
                                        {isSaving ? 'Saving...' : 'Save Lore'}
                                    </button>
                                </>
                            ) : canEdit ? (
                                <button
                                    onClick={() => setIsEditMode(true)}
                                    className="px-5 py-2 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 rounded-lg font-serif font-medium transition-all shadow-md border border-amber-500/30 text-white"
                                >
                                    {loreEntry ? 'Edit Lore' : 'Write Lore'}
                                </button>
                            ) : (
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 bg-amber-200/50 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors font-serif border border-amber-400/30"
                                >
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Delete confirmation overlay */}
                {showDeleteConfirm && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div
                            className="bg-amber-50 rounded-lg p-6 max-w-sm mx-4 shadow-xl"
                            style={{
                                border: '2px solid rgba(139,90,43,0.3)',
                            }}
                        >
                            <h3 className="text-lg font-serif text-amber-900 mb-3">Delete Lore?</h3>
                            <p className="text-sm text-amber-800/70 font-serif mb-4">
                                This will permanently remove the lore entry at this location.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="px-4 py-2 bg-amber-200/50 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors font-serif border border-amber-400/30"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    className="px-4 py-2 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 disabled:opacity-60 rounded-lg font-serif text-white shadow-md"
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LoreModal;
